import Container from 'components/container/container';
import ProductPage from 'components/product';
import {
  ProductDivider,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
  ProductPlan,
  ProductQuote,
} from 'components/product/product.styles';
import { ProductFeatureStatus } from 'components/product/product.types';
import Link from 'next/link';
import { useState } from 'react';
import Plan from 'shared/components/plan';

import { CUBUILD_URL, LIBUM_FEEDBACK, LIBUM_URL, MIKE_LINKEDIN, VSCODE_URL } from 'shared/constants/globals';
import { PRODUCTS } from 'shared/data/products';

const POWERON_PRO = PRODUCTS.find((p) => p.id === 'poweron-pro')!;

export default function PowerOnPro() {
  const title = 'PowerOn Pro | Libum';
  const description = 'PowerOn Pro is your complete development toolkit for PowerOn, within Visual Studio Code.';
  const keywords =
    'PowerOn Pro,PowerOn extension,PowerOn development,PowerOn language server,PowerOn vscode,Symitar tools,PowerOn,Symitar,Symitar software,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/poweron-pro`;

  const [showMore, setShowMore] = useState(false);
  const handleShowMoreClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setShowMore(!showMore);
  };

  const getStatusDisplayName = (status: ProductFeatureStatus): string => {
    return status
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="poweron-pro">
      <div style={{ width: '100%' }}>
        <ProductPage product={POWERON_PRO}>
          <h1>PowerOn Pro</h1>
          <h2>Your development toolkit for PowerOn, within Visual Studio Code.</h2>
          <ProductDivider />
          <p id="overview">
            We thought it&apos;d be awesome to build out a complete language server for PowerOn and couple it with
            direct Symitar integration. At{' '}
            <Link href={CUBUILD_URL} target="_blank">
              CU Build
            </Link>{' '}
            in 2024, we decided to take this on and make it available in one of the world&apos;s most popular IDEs,{' '}
            <Link href={VSCODE_URL} target="_blank">
              Visual Studio Code
            </Link>{' '}
            — and thus, PowerOn Pro was born.
          </p>
          <p>
            Shortly thereafter, we asked the community to use their imagination for features they&apos;d like to see
            added to the extension and we&apos;ve been working on those ever since.
          </p>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured">
              {POWERON_PRO.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`isFeatured ${feature.docsLink ? 'has-link' : ''}`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="image">
                      {/* eslint-disable-next-line */}
                      <img src={feature.imagePath} style={{ maxHeight: '100%' }} />
                    </div>
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {POWERON_PRO.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid className={showMore ? 'opened' : 'hidden'}>
              {POWERON_PRO.features
                .filter((f) => f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="details">
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <Link href="" passHref legacyBehavior>
              <ProductFeatureShowMoreLink onClick={(e: any) => handleShowMoreClick(e)}>
                {showMore ? 'Show less' : 'Show more'}
              </ProductFeatureShowMoreLink>
            </Link>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <ProductQuote>
            &quot;Libum&apos;s development team knows their stuff. Even while the extension was in Alpha, most of the
            features they promised worked well and those that did not, the team addressed them right away.
            <br />
            <br />
            This extension has saved me a lot of development time because of its ease of use and smooth integration with
            Symitar. It&apos;s very convenient as you can get away with not having to switch from VS Code to Symitar
            Quest and back again to troubleshoot your applications.
            <br />
            <br />
            Did I mention, great technical support team?&quot;
            <br />
            <br />{' '}
            <i>
              <Link href={MIKE_LINKEDIN} target="_blank">
                Louie Medina, Developer @ Citadel Credit Union
              </Link>
            </i>
          </ProductQuote>
          <ProductDivider />
          <ProductPlan id="offerings">
            <Plan
              header={POWERON_PRO.plan.header}
              features={POWERON_PRO.plan.features}
              offerings={POWERON_PRO.plan.offerings}
            />
          </ProductPlan>
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in PowerOn Pro?</h3>
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Submit Your Idea
            </Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
